'use client';

import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useRouter } from 'next/navigation';
import { Eye, EyeOff, Mail, Lock, User, Copy, Check, ArrowRight, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import AppLogo from '@/components/ui/AppLogo';
import { Toaster } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

interface SignInForm {
  email: string;
  password: string;
  rememberMe: boolean;
}

interface SignUpForm {
  fullName: string;
  email: string;
  password: string;
  confirmPassword: string;
  agreeTerms: boolean;
}

const demoCredentials = [
  { role: 'Member', email: 'morgan.chen@nexora.health', password: 'NexoraMember2026!' },
  { role: 'Advisor', email: 'dr.sarah.walsh@nexora.health', password: 'NexoraAdvisor2026!' },
  { role: 'Admin', email: 'admin@holistic.app', password: 'NexoraAdmin2026!' },
];

interface Props {
  mode: 'signin' | 'signup';
  onModeChange: (m: 'signin' | 'signup') => void;
}

export default function AuthFormPanel({ mode, onModeChange }: Props) {
  const router = useRouter();
  const { signIn, signUp } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const signInForm = useForm<SignInForm>({
    defaultValues: { email: '', password: '', rememberMe: false },
  });

  const signUpForm = useForm<SignUpForm>({
    defaultValues: { fullName: '', email: '', password: '', confirmPassword: '', agreeTerms: false },
  });

  const handleCopy = async (value: string, field: string) => {
    await navigator.clipboard.writeText(value);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const handleUseCredential = (cred: typeof demoCredentials[0]) => {
    if (mode === 'signin') {
      signInForm.setValue('email', cred.email);
      signInForm.setValue('password', cred.password);
      toast.success(`Demo credentials loaded for ${cred.role}`);
    }
  };

  const onSignIn = async (data: SignInForm) => {
    setIsLoading(true);
    try {
      await signIn(data.email, data.password);
      toast.success('Welcome back! Signing you in...');
      router.push('/member-dashboard-overview');
    } catch (error: any) {
      const message =
        error?.message === 'Invalid login credentials' ?'Invalid email or password — check your credentials or use a demo account below' : error?.message ||'Sign in failed. Please try again.';
      signInForm.setError('email', { message });
    } finally {
      setIsLoading(false);
    }
  };

  const onSignUp = async (data: SignUpForm) => {
    if (data.password !== data.confirmPassword) {
      signUpForm.setError('confirmPassword', { message: 'Passwords do not match' });
      return;
    }
    setIsLoading(true);
    try {
      await signUp(data.email, data.password, { fullName: data.fullName, role: 'member' });
      toast.success('Account created! Starting your onboarding journey...');
      router.push('/onboarding-wizard');
    } catch (error: any) {
      const message =
        error?.message?.includes('already registered')
          ? 'An account with this email already exists. Please sign in instead.' : error?.message ||'Sign up failed. Please try again.';
      signUpForm.setError('email', { message });
    } finally {
      setIsLoading(false);
    }
  };

  if (!mounted) {
    return (
      <div className="flex-1 flex flex-col overflow-y-auto bg-[hsl(220,50%,3%)]">
        <div className="flex-1 flex items-center justify-center px-6 py-10">
          <div className="w-full max-w-md">
            <div className="h-12 rounded-xl bg-[hsl(215,28%,10%)] border border-[hsl(215,20%,18%)] mb-8 animate-pulse" />
            <div className="h-8 w-48 rounded bg-[hsl(215,28%,10%)] mb-2 animate-pulse" />
            <div className="h-4 w-64 rounded bg-[hsl(215,28%,10%)] mb-8 animate-pulse" />
            <div className="space-y-4">
              <div className="h-12 rounded-xl bg-[hsl(215,28%,10%)] animate-pulse" />
              <div className="h-12 rounded-xl bg-[hsl(215,28%,10%)] animate-pulse" />
              <div className="h-12 rounded-xl bg-[#6C5CE7]/40 animate-pulse" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col overflow-y-auto bg-[hsl(220,50%,3%)]">
      <Toaster position="top-right" theme="dark" richColors />

      {/* Mobile logo */}
      <div className="lg:hidden flex items-center gap-2.5 px-6 pt-8 pb-2">
        <AppLogo size={32} />
        <span className="font-display font-semibold text-base tracking-tight">
          HolisticAI<span className="bg-gradient-to-r from-[#6C5CE7] to-[#00F5A0] bg-clip-text text-transparent">.Health</span>
        </span>
      </div>

      <div className="flex-1 flex items-center justify-center px-6 py-10">
        <div className="w-full max-w-md animate-slide-up">
          {/* Mode toggle */}
          <div className="flex items-center gap-1 p-1 rounded-xl bg-[hsl(215,28%,10%)] border border-[hsl(215,20%,18%)] mb-8">
            <button
              type="button"
              onClick={() => onModeChange('signin')}
              className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                mode === 'signin' ? 'bg-[#6C5CE7] text-white shadow-glow' : 'text-[hsl(215,15%,60%)] hover:text-[hsl(210,40%,96%)]'
              }`}
            >
              Sign In
            </button>
            <button
              type="button"
              onClick={() => onModeChange('signup')}
              className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                mode === 'signup' ? 'bg-[#6C5CE7] text-white shadow-glow' : 'text-[hsl(215,15%,60%)] hover:text-[hsl(210,40%,96%)]'
              }`}
            >
              Create Account
            </button>
          </div>

          {/* Heading */}
          <div className="mb-8">
            <h2 className="font-display text-2xl font-bold text-[hsl(210,40%,96%)]">
              {mode === 'signin' ? 'Welcome back' : 'Start your journey'}
            </h2>
            <p className="mt-1.5 text-sm text-[hsl(215,15%,55%)]">
              {mode === 'signin' ? 'Sign in to your HolisticAI.Health dashboard' : 'Create your account and complete your health dossier'}
            </p>
          </div>

          {/* Google OAuth */}
          <button
            type="button"
            className="w-full flex items-center justify-center gap-3 px-4 py-3 rounded-xl border border-[hsl(215,20%,22%)] bg-[hsl(215,28%,12%)] hover:bg-[hsl(215,28%,15%)] hover:border-[hsl(215,20%,30%)] transition-all duration-200 text-sm font-medium text-[hsl(210,40%,90%)] active:scale-[0.98] mb-5"
            onClick={() => toast.info('Google OAuth — configure in Supabase Auth dashboard')}
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continue with Google
          </button>

          {/* Divider */}
          <div className="flex items-center gap-3 mb-5">
            <div className="flex-1 h-px bg-[hsl(215,20%,18%)]" />
            <span className="text-xs text-[hsl(215,15%,45%)] font-medium">or continue with email</span>
            <div className="flex-1 h-px bg-[hsl(215,20%,18%)]" />
          </div>

          {/* Sign In Form */}
          {mode === 'signin' && (
            <form onSubmit={signInForm.handleSubmit(onSignIn)} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[hsl(210,40%,85%)] mb-1.5">
                  Email address
                </label>
                <div className="relative">
                  <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[hsl(215,15%,45%)]" />
                  <input
                    type="email"
                    placeholder="you@example.com"
                    autoComplete="email"
                    {...signInForm.register('email', {
                      required: 'Email is required',
                      pattern: { value: /\S+@\S+\.\S+/, message: 'Invalid email address' },
                    })}
                    className="w-full pl-10 pr-4 py-3 rounded-xl bg-[hsl(215,28%,10%)] border border-[hsl(215,20%,20%)] text-sm text-[hsl(210,40%,92%)] placeholder:text-[hsl(215,15%,38%)] focus:outline-none focus:border-[#6C5CE7] focus:ring-1 focus:ring-[#6C5CE7]/30 transition-all duration-200"
                  />
                </div>
                {signInForm.formState.errors.email && (
                  <p className="mt-1.5 text-xs text-[hsl(0,72%,60%)]">
                    {signInForm.formState.errors.email.message}
                  </p>
                )}
              </div>

              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="block text-sm font-medium text-[hsl(210,40%,85%)]">Password</label>
                  <button type="button" className="text-xs text-[#8B7CFF] hover:text-[#6C5CE7] transition-colors">
                    Forgot password?
                  </button>
                </div>
                <div className="relative">
                  <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[hsl(215,15%,45%)]" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    autoComplete="current-password"
                    {...signInForm.register('password', {
                      required: 'Password is required',
                      minLength: { value: 6, message: 'Minimum 6 characters' },
                    })}
                    className="w-full pl-10 pr-11 py-3 rounded-xl bg-[hsl(215,28%,10%)] border border-[hsl(215,20%,20%)] text-sm text-[hsl(210,40%,92%)] placeholder:text-[hsl(215,15%,38%)] focus:outline-none focus:border-[#6C5CE7] focus:ring-1 focus:ring-[#6C5CE7]/30 transition-all duration-200"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3.5 top-1/2 -translate-y-1/2 text-[hsl(215,15%,45%)] hover:text-[hsl(210,40%,80%)] transition-colors"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {signInForm.formState.errors.password && (
                  <p className="mt-1.5 text-xs text-[hsl(0,72%,60%)]">
                    {signInForm.formState.errors.password.message}
                  </p>
                )}
              </div>

              <div className="flex items-center gap-2.5">
                <input
                  type="checkbox"
                  id="rememberMe"
                  autoComplete="off"
                  {...signInForm.register('rememberMe')}
                  className="w-4 h-4 rounded border-[hsl(215,20%,30%)] bg-[hsl(215,28%,10%)] accent-[#6C5CE7]"
                />
                <label htmlFor="rememberMe" className="text-sm text-[hsl(215,15%,60%)]">
                  Remember me for 30 days
                </label>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl bg-[#6C5CE7] hover:bg-[#7C6CF7] active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold text-sm transition-all duration-200 shadow-glow mt-2"
              >
                {isLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    Sign In to Dashboard
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </form>
          )}

          {/* Sign Up Form */}
          {mode === 'signup' && (
            <form onSubmit={signUpForm.handleSubmit(onSignUp)} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[hsl(210,40%,85%)] mb-1.5">Full name</label>
                <div className="relative">
                  <User className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[hsl(215,15%,45%)]" />
                  <input
                    type="text"
                    placeholder="Morgan Chen"
                    autoComplete="name"
                    {...signUpForm.register('fullName', { required: 'Full name is required' })}
                    className="w-full pl-10 pr-4 py-3 rounded-xl bg-[hsl(215,28%,10%)] border border-[hsl(215,20%,20%)] text-sm text-[hsl(210,40%,92%)] placeholder:text-[hsl(215,15%,38%)] focus:outline-none focus:border-[#6C5CE7] focus:ring-1 focus:ring-[#6C5CE7]/30 transition-all duration-200"
                  />
                </div>
                {signUpForm.formState.errors.fullName && (
                  <p className="mt-1.5 text-xs text-[hsl(0,72%,60%)]">{signUpForm.formState.errors.fullName.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-[hsl(210,40%,85%)] mb-1.5">Email address</label>
                <div className="relative">
                  <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[hsl(215,15%,45%)]" />
                  <input
                    type="email"
                    placeholder="you@example.com"
                    autoComplete="email"
                    {...signUpForm.register('email', {
                      required: 'Email is required',
                      pattern: { value: /\S+@\S+\.\S+/, message: 'Invalid email address' },
                    })}
                    className="w-full pl-10 pr-4 py-3 rounded-xl bg-[hsl(215,28%,10%)] border border-[hsl(215,20%,20%)] text-sm text-[hsl(210,40%,92%)] placeholder:text-[hsl(215,15%,38%)] focus:outline-none focus:border-[#6C5CE7] focus:ring-1 focus:ring-[#6C5CE7]/30 transition-all duration-200"
                  />
                </div>
                {signUpForm.formState.errors.email && (
                  <p className="mt-1.5 text-xs text-[hsl(0,72%,60%)]">{signUpForm.formState.errors.email.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-[hsl(210,40%,85%)] mb-1.5">Password</label>
                <div className="relative">
                  <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[hsl(215,15%,45%)]" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Min. 8 characters"
                    autoComplete="new-password"
                    {...signUpForm.register('password', {
                      required: 'Password is required',
                      minLength: { value: 8, message: 'Minimum 8 characters' },
                    })}
                    className="w-full pl-10 pr-11 py-3 rounded-xl bg-[hsl(215,28%,10%)] border border-[hsl(215,20%,20%)] text-sm text-[hsl(210,40%,92%)] placeholder:text-[hsl(215,15%,38%)] focus:outline-none focus:border-[#6C5CE7] focus:ring-1 focus:ring-[#6C5CE7]/30 transition-all duration-200"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3.5 top-1/2 -translate-y-1/2 text-[hsl(215,15%,45%)] hover:text-[hsl(210,40%,80%)] transition-colors"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {signUpForm.formState.errors.password && (
                  <p className="mt-1.5 text-xs text-[hsl(0,72%,60%)]">{signUpForm.formState.errors.password.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-[hsl(210,40%,85%)] mb-1.5">Confirm password</label>
                <div className="relative">
                  <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[hsl(215,15%,45%)]" />
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    placeholder="Re-enter password"
                    autoComplete="new-password"
                    {...signUpForm.register('confirmPassword', { required: 'Please confirm your password' })}
                    className="w-full pl-10 pr-11 py-3 rounded-xl bg-[hsl(215,28%,10%)] border border-[hsl(215,20%,20%)] text-sm text-[hsl(210,40%,92%)] placeholder:text-[hsl(215,15%,38%)] focus:outline-none focus:border-[#6C5CE7] focus:ring-1 focus:ring-[#6C5CE7]/30 transition-all duration-200"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3.5 top-1/2 -translate-y-1/2 text-[hsl(215,15%,45%)] hover:text-[hsl(210,40%,80%)] transition-colors"
                  >
                    {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {signUpForm.formState.errors.confirmPassword && (
                  <p className="mt-1.5 text-xs text-[hsl(0,72%,60%)]">{signUpForm.formState.errors.confirmPassword.message}</p>
                )}
              </div>

              <div className="flex items-start gap-2.5">
                <input
                  type="checkbox"
                  id="agreeTerms"
                  autoComplete="off"
                  {...signUpForm.register('agreeTerms', { required: 'You must agree to the terms' })}
                  className="w-4 h-4 mt-0.5 rounded border-[hsl(215,20%,30%)] bg-[hsl(215,28%,10%)] accent-[#6C5CE7]"
                />
                <label htmlFor="agreeTerms" className="text-xs text-[hsl(215,15%,55%)] leading-relaxed">
                  I agree to the{' '}
                  <button type="button" className="text-[#8B7CFF] hover:underline">Terms of Service</button>
                  {' '}and{' '}
                  <button type="button" className="text-[#8B7CFF] hover:underline">Privacy Policy</button>
                  . This platform is for educational and wellness purposes only.
                </label>
              </div>
              {signUpForm.formState.errors.agreeTerms && (
                <p className="text-xs text-[hsl(0,72%,60%)]">{signUpForm.formState.errors.agreeTerms.message}</p>
              )}

              <button
                type="submit"
                disabled={isLoading}
                className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl bg-[#6C5CE7] hover:bg-[#7C6CF7] active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold text-sm transition-all duration-200 shadow-glow mt-2"
              >
                {isLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    Create Account & Start Onboarding
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </form>
          )}

          {/* Demo Credentials */}
          <div className="mt-8 rounded-xl border border-[#6C5CE7]/25 bg-[#6C5CE7]/5 overflow-hidden">
            <div className="px-4 py-3 border-b border-[#6C5CE7]/20 flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-[#00F5A0] animate-pulse" />
              <span className="text-xs font-semibold text-[hsl(210,40%,80%)] tracking-wide uppercase">Demo Accounts</span>
            </div>
            <div className="p-3 space-y-2">
              {demoCredentials.map((cred) => (
                <div
                  key={`cred-${cred.role}`}
                  className="flex items-center gap-2 px-3 py-2.5 rounded-lg bg-[hsl(215,28%,10%)] border border-[hsl(215,20%,18%)] hover:border-[#6C5CE7]/40 transition-colors group"
                >
                  <span className="text-xs font-semibold text-[#8B7CFF] w-14 flex-shrink-0">{cred.role}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-[hsl(210,40%,75%)] truncate font-mono-num">{cred.email}</p>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => handleCopy(cred.email, `${cred.role}-email`)}
                      className="p-1 rounded text-[hsl(215,15%,45%)] hover:text-[#8B7CFF] transition-colors"
                      title="Copy email"
                    >
                      {copiedField === `${cred.role}-email` ? (
                        <Check className="w-3 h-3 text-[#00F5A0]" />
                      ) : (
                        <Copy className="w-3 h-3" />
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleUseCredential(cred)}
                      className="px-2 py-1 rounded text-xs font-medium text-[#6C5CE7] hover:text-white hover:bg-[#6C5CE7] transition-all duration-150 border border-[#6C5CE7]/40"
                    >
                      Use
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Switch mode link */}
          <p className="text-center text-sm text-[hsl(215,15%,50%)] mt-6">
            {mode === 'signin' ? "Don't have an account? " : 'Already have an account? '}
            <button
              type="button"
              onClick={() => onModeChange(mode === 'signin' ? 'signup' : 'signin')}
              className="text-[#8B7CFF] hover:text-[#6C5CE7] font-medium transition-colors"
            >
              {mode === 'signin' ? 'Create one free' : 'Sign in'}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}